// ============================================================================
// ThemeContext — Light/dark mode toggle with localStorage persistence
// ============================================================================

import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { createBeoflowTheme } from '@/theme/muiTheme'
import { isMobileWebViewAuthSession } from '@/contexts/AuthContext'

type ThemeMode = 'light' | 'dark'

const STORAGE_KEY = 'beoflow-theme-mode'

function getInitialMode(): ThemeMode {
  // The native apps are dark-chrome shells: force dark inside them so the
  // web surfaces (Flow chat, Calendar, …) match, regardless of any stored
  // light preference from the web.
  if (isMobileWebViewAuthSession()) return 'dark'
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch { /* ignore */ }
  return 'light'
}

interface ThemeModeContextValue {
  mode: ThemeMode
  toggleTheme: () => void
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: 'light',
  toggleTheme: () => {},
})

export function BeoflowThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode)

  const toggleTheme = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light'
      try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
      return next
    })
  }, [])

  const theme = useMemo(() => createBeoflowTheme(mode), [mode])

  return (
    <ThemeModeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeModeContext.Provider>
  )
}

export function useThemeMode() {
  return useContext(ThemeModeContext)
}
